import { gql } from "@apollo/client";

export const GET_COMPANY = gql`
  query getCompany($id: ID!) {
    getCompany(id: $id) {
      id
      name
      intracomVatNr
      euVatNumber
      capitalAmount
      vatNumber
      internalReference
      analyticalLevel
      hasNotAccountingFirm
      accountingFirm {
        id
        name
      }
      legalStructure {
        code
        name
        country {
          code
          name
        }
      }
      businessActivity {
        code
        name
      }
      legalCategory {
        code
        name
      }
      businessCode
      phone1
      phone2
      email
      fax
      fiscal {
        declareTva
      }
      settings {
        canExport
        software {
          software {
            label
            value
          }
          name
        }
        accountingPlanType {
          value
        }
      }
      displayName
      rcs
      addresses {
        id
        address1
        address2
        addressNr
        addressBtq
        postalCode
        city
        country {
          code
          name
        }
      }
    }
  }
`;

export const GET_COMPANY_SETTINGS = gql`
  query getCompanySetting($id: ID!) {
    getCompanySetting(id: $id) {
      availabilityTime
      showDocumentDescription
      defaultPriceType {
        label
        value
      }
      analyticalType
      counterpartProductAllocationType {
        label
        value
      }
      budgetFirstNumber: budgetNextNumber
      budgetFormat
      clientFormat
      deliveryFormFirstNumber: deliveryFormNextNumber
      deliveryFormFormat
      purchaseOrderFirstNumber: purchaseOrderNextNumber
      purchaseOrderFormat
      returnFirstNumber: returnNextNumber
      returnFormat
      receiptFirstNumber: receiptNextNumber
      receiptFormat
      saleOrderFirstNumber: saleOrderNextNumber
      saleOrderFormat
      canChangeBudgetNextNumber
      canChangeDeliveryFormNextNumber
      canChangePurchaseOrderNextNumber
      canChangePaymentInAdvanceNextNumber
      canChangeReturnNextNumber
      canChangeSaleOrderNextNumber
      paElectronicAddressData {
        id
        paElectronicAddress
      }
      setupFinished
      defaultBank {
        id
        name
      }
      defaultPaymentMethods {
        id
        code
        name
      }
      saleDocumentTypes {
        label
        value
      }
      hasCurrencyWithValidatedDocuments
      hasValidatedDocuments
      paymentInAdvanceFirstNumber
      paymentInAdvanceFormat
      clientHasAutoNumber
      sendOverdueEmail
      defaultBankAccount {
        id
        name
        sepaCredentialIdentifier
        iban
        bic
      }
      decimalPlaces
      decimalPlacesForQuantity
      unitMeasures {
        id
        name
        slug
      }
      currencySettings {
        id
        decimalPlaces
        code
        isMainCurrency
      }
    }
  }
`;

export const UPDATE_COMPANY = gql`
  mutation updateCompany($input: UpdateCompanyInput!) {
    updateCompany(input: $input) {
      success
      errors {
        field
        messages
      }
    }
  }
`;

export const UPDATE_COMPANY_ADDRESS = gql`
  mutation updateAddress($input: UpdateAddressInput!) {
    updateAddress(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const CREATE_COMPANY_ADDRESS = gql`
  mutation createAddress($input: CreateAddressInput!) {
    createAddress(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const UPDATE_COMPANY_SETTINGS = gql`
  mutation updateCompanySetting($input: CompanySettingInput!) {
    updateCompanySetting(input: $input) {
      companySetting {
        availabilityTime
        defaultPriceType {
          label
          value
        }
        analyticalType
        counterpartProductAllocationType {
          label
          value
        }
        budgetFirstNumber: budgetNextNumber
        budgetFormat
        clientFormat
        deliveryFormFirstNumber: deliveryFormNextNumber
        deliveryFormFormat
        purchaseOrderFirstNumber: purchaseOrderNextNumber
        purchaseOrderFormat
        returnFirstNumber: returnNextNumber
        returnFormat
        showDocumentDescription
        receiptFirstNumber: receiptNextNumber
        receiptFormat
        saleOrderFirstNumber: saleOrderNextNumber
        saleOrderFormat
        canChangeBudgetNextNumber
        canChangeDeliveryFormNextNumber
        canChangePurchaseOrderNextNumber
        canChangePaymentInAdvanceNextNumber
        canChangeReturnNextNumber
        canChangeSaleOrderNextNumber
        hasCurrencyWithValidatedDocuments
        hasValidatedDocuments
        paymentInAdvanceFirstNumber
        paymentInAdvanceFormat
        paElectronicAddressData {
          id
          paElectronicAddress
        }
        setupFinished
        clientHasAutoNumber
        sendOverdueEmail
        defaultBank {
          id
          name
        }
        defaultPaymentMethods {
          id
          code
          name
        }
        saleDocumentTypes {
          label
          value
        }
        defaultBankAccount {
          id
          name
          sepaCredentialIdentifier
          iban
          bic
        }
        decimalPlaces
        decimalPlacesForQuantity
        unitMeasures {
          id
          name
          slug
        }
      }
      errors {
        field
        messages
      }
      success
    }
  }
`;

export const LIST_ACCOUNTING_FIRM_COMPANIES = gql`
  query listAccountingFirmCompanies($accountingFirmId: ID, $id_In: [ID]) {
    listCompaniesPaginated(filter: { accountingFirm: $accountingFirmId, id_In: $id_In }) {
      results(limit: 2, orderBy: ["name"]) {
        id
        name
        internalReference
        accountingFirm {
          id
          name
        }
      }
      totalCount
    }
  }
`;

export const LIST_COMPANIES_QUERY = gql`
  query listCompaniesPaginated($filter: CompanyFilter, $limit: Int, $offset: Int) {
    listCompaniesPaginated(filter: $filter) {
      results(limit: $limit, offset: $offset, orderBy: "name") {
        id
        name
        internalReference
        accountingFirm {
          id
          name
        }
        fiscal {
          declareTva
        }
      }
      totalCount
    }
  }
`;

export const LIST_PAYMENT_METHODS = gql`
  query listPaymentMethods(
    $limit: Int
    $offset: Int
    $orderBy: [String]
    $filter: PaymentMethodFilter
  ) {
    listPaymentMethods(filter: $filter) {
      totalCount
      results(limit: $limit, offset: $offset, orderBy: $orderBy) {
        code
        name
        isActive
        id
        companypaymentmethodSet {
          paymentMethod {
            id
          }
          balanceType {
            label
            value
          }
          application {
            label
            value
          }
        }
        order
      }
    }
  }
`;

export const LIST_DEFAULT_PAYMENT_METHODS = gql`
  query listPaymentMethods(
    $limit: Int
    $offset: Int
    $orderBy: [String]
    $filter: PaymentMethodFilter
  ) {
    listPaymentMethods(filter: $filter) {
      totalCount
      results(limit: $limit, offset: $offset, orderBy: $orderBy) {
        id
        name
        code
      }
    }
  }
`;

export const LIST_BUSSINESS_ACTIVITIES = gql`
  query listBusinessActivities($filter: BusinessActivityFilter) {
    listBusinessActivities(filter: $filter) {
      results {
        code
        name
      }
    }
  }
`;

export const LIST_COMPANIES_PAGINATED = gql`
  query listCompaniesPaginated(
    $limit: Int
    $offset: Int
    $orderBy: [String]
    $filter: CompanyFilter
  ) {
    listCompaniesPaginated(filter: $filter) {
      totalCount
      results(limit: $limit, offset: $offset, orderBy: $orderBy) {
        id
        name
        internalReference
        accountingFirm {
          id
          name
        }
      }
    }
  }
`;

export const LIST_PLATFORMS = gql`
  query listPlatforms($filter: PlatformFilter) {
    listPlatforms(filter: $filter) {
      results(limit: 100, offset: 0) {
        id
        name
        software {
          label
          value
        }
        defaultPrefixClientAccount
      }
    }
  }
`;

export const GET_COMPANY_SETTINGS_CENTRAL_API = gql`
  query getCompanySettings($id: ID!) {
    getCompanySettings(id: $id) {
      ssiType {
        label
        value
      }
      canExport
      allowAccountCreationsInSsi
      generalAccountsLength
      thirdPartiesAccountsLength
      prefixClientAccount
      prefixProviderAccount
      collectiveClientAccount
      collectiveProviderAccount
      software {
        defaultPrefixClientAccount
        defaultPrefixProviderAccount
        collectiveClientAccount
        collectiveProviderAccount
        software {
          label
          value
        }
        id
        name
      }
    }
  }
`;

export const UPDATE_COMPANY_SETTINGS_CENTRAL_API = gql`
  mutation updateCompanySettings($input: CompanySettingInput!) {
    updateCompanySettings(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const LIST_PA_ELECTRONIC_ADDRESSES = gql`
  query listPaElectronicAddresses(
    $filter: PaElectronicAddressFilter
    $limit: Int
    $offset: Int
    $orderBy: [String]
  ) {
    listPaElectronicAddresses(filter: $filter) {
      totalCount
      results(limit: $limit, offset: $offset, orderBy: $orderBy) {
        id
        paElectronicAddress
      }
    }
  }
`;
